





























































import axios, { AxiosResponse } from 'axios';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import { settlePromises } from '@/utils/promise';
import { createImageUploadFormData } from '@/utils/imageUpload';
import CoButton from '../../../../Atoms/co-button/CoButton.vue';
import CoSkeleton from '../../../../Atoms/co-skeleton/CoSkeleton.vue';
import CoBookingResource from '../../../../Organisms/co-booking-resource/CoBookingResource.vue';
import EventBus from '../../../../../eventBus';
import {
    BookingResource,
    BookingResourceBookingTime,
    BookingResourceCalendarToken,
    BookingResourceExternalCalendar,
    BookingResourceLocationOption,
    BookingResourceLockOption,
    BookingResourceTaxRate,
    BookingResourceUpgrade,
    BookingGranularityType,
    CheckOutDayType,
    cloneBookingResource,
    createBookingResource,
} from '../../../../Organisms/co-booking-resource/models.ts';

const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

export default {
    name: 'BookingResourceCreateEdit',
    components: {
        CoBookingResource,
        CoButton,
        CoHeadline,
        CoSkeleton,
    },
    data() {
        return {
            loadingInitialData: true,
            saving: false,
            resourceLoaded: false,
            resource: createBookingResource() as BookingResource,
            locations: [] as BookingResourceLocationOption[],
            taxRates: [] as BookingResourceTaxRate[],
            resources: [] as BookingResource[],
            calendarTokens: [] as BookingResourceCalendarToken[],
            calendarOptions: [] as BookingResourceExternalCalendar[],
            availableLocks: [] as BookingResourceLockOption[],
            availableUpgrades: [] as BookingResourceUpgrade[],
            plans: [] as Array<{ Active?: boolean }>,
            selectedImageFile: null as File | null,
        };
    },
    computed: {
        isEditMode(): boolean {
            return !!this.$route.params.id;
        },
        currency(): string {
            return this.$store.state.space?.Currency || this.resource.Currency || 'EUR';
        },
        billingEnabled(): boolean {
            return !!this.$store.state.billingTurnedOn;
        },
        hasActivePlans(): boolean {
            return !!this.plans.find((plan) => plan.Active);
        },
        upgradesEnabled(): boolean {
            return !!this.$unleash?.isEnabled('frontend.booking-upgrades');
        },
        permissionsEnabled(): boolean {
            return !!this.$unleash?.isEnabled('frontend.booking-resource-permissions');
        },
        locksEnabled(): boolean {
            return !!this.$unleash?.isEnabled('frontend.locks-for-booking-resources.admin');
        },
        pageTitle() {
            if (this.isEditMode) {
                return `${this.$t('labels.bookingresourceedit')}`;
            }

            return `${this.$t('labels.bookingresourcecreate')}`;
        },
        submitLabel() {
            return this.isEditMode ? this.$t('labels.save') : this.$t('labels.create');
        },
    },
    created() {
        this.initialize();
    },
    methods: {
        async initialize() {
            this.loadingInitialData = true;

            const requests = [
                this.listLocations(),
                this.getTaxRates(),
                this.listResources(),
                this.getCalendarTokens(),
                this.fetchPlans(),
                this.getUpgrades(),
            ];

            if (this.locksEnabled) {
                requests.push(this.listLocks());
            }

            if (this.isEditMode) {
                requests.push(this.getResource());
            } else if (this.$store.state.resourceDuplicate) {
                this.resource = this.normalizeResource(this.$store.state.resourceDuplicate);
                this.$store.state.resourceDuplicate = null;
                this.resourceLoaded = true;
            } else {
                this.resource = this.createDefaultResource();
                this.resourceLoaded = true;
            }

            await settlePromises(requests);
            this.loadingInitialData = false;
        },
        createDefaultResource(): BookingResource {
            return createBookingResource({
                Currency: this.currency,
            });
        },
        normalizeBookingTimeOverride(bookingTimeOverride) {
            return Object.keys(bookingTimeOverride || {}).reduce((result, key) => {
                const currentValue = bookingTimeOverride[key] || {};

                return {
                    ...result,
                    [key]: {
                        From: currentValue.From || currentValue.from || '09:00',
                        To: currentValue.To || currentValue.to || '17:00',
                        Block: !!currentValue.Block,
                        IsAllDay: currentValue.IsAllDay !== undefined ? !!currentValue.IsAllDay : !!currentValue.Block,
                    },
                };
            }, {});
        },
        normalizeBookingTimes(bookingTimes, resource) {
            if (!Array.isArray(bookingTimes)) {
                // Loaded resources may omit recurring availability; keep that as no weekly availability.
                return [];
            }

            const normalizedBookingTimes = bookingTimes.map((bookingTime) => ({
                ...bookingTime,
                // Empty Weekdays is a valid unavailable state; only legacy missing values fall back to all days.
                Weekdays: Array.isArray(bookingTime.Weekdays)
                    ? [...bookingTime.Weekdays].sort((left, right) => left - right)
                    : ALL_WEEKDAYS,
            }));

            return this.removeSyntheticFollowingDayCheckoutBookingTime(normalizedBookingTimes, resource);
        },
        normalizeResource(rawResource): BookingResource {
            return createBookingResource({
                ...rawResource,
                Currency: this.currency,
                BookingTimes: this.normalizeBookingTimes(rawResource.BookingTimes, rawResource),
                BookingTimeOverride: this.normalizeBookingTimeOverride(rawResource.BookingTimeOverride),
            });
        },
        isDayGranularityFollowingDayCheckout(resource: BookingResource): boolean {
            return (
                resource?.BookingGranularity === BookingGranularityType.BookingGranularityDay &&
                resource?.DayBooking?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay
            );
        },
        formatDayBookingTime(timeOfDay): string {
            const hour = Number(timeOfDay?.Hour) || 0;
            const minute = Number(timeOfDay?.Minute) || 0;
            return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        },
        getNextWeekday(weekday: number): number {
            return weekday === 7 ? 1 : weekday + 1;
        },
        getWeekdaySet(bookingTimes: BookingResourceBookingTime[]): Set<number> {
            return bookingTimes.reduce((weekdays, bookingTime) => {
                (bookingTime.Weekdays || []).forEach((weekday) => weekdays.add(weekday));
                return weekdays;
            }, new Set<number>());
        },
        buildSyntheticFollowingDayCheckoutBookingTime(bookingTimes: BookingResourceBookingTime[], resource) {
            if (!this.isDayGranularityFollowingDayCheckout(resource)) {
                return null;
            }

            const checkoutTime = this.formatDayBookingTime(resource?.DayBooking?.CheckOutTime);
            if (checkoutTime === '00:00') {
                return null;
            }

            const selectedWeekdays = this.getWeekdaySet(bookingTimes);
            const hiddenWeekdays = [...selectedWeekdays]
                .map((weekday) => this.getNextWeekday(weekday))
                .filter(
                    (weekday, index, weekdays) => !selectedWeekdays.has(weekday) && weekdays.indexOf(weekday) === index
                )
                .sort((left, right) => left - right);

            if (hiddenWeekdays.length === 0) {
                return null;
            }

            // Backend availability needs checkout-only mornings for days that stay hidden in the admin selector.
            return {
                From: '00:00',
                To: checkoutTime,
                Weekdays: hiddenWeekdays,
            };
        },
        hasSameWeekdays(left: number[] = [], right: number[] = []): boolean {
            if (left.length !== right.length) {
                return false;
            }

            return left.every((weekday, index) => weekday === right[index]);
        },
        removeSyntheticFollowingDayCheckoutBookingTime(bookingTimes: BookingResourceBookingTime[], resource) {
            if (!this.isDayGranularityFollowingDayCheckout(resource)) {
                return bookingTimes;
            }

            const checkoutTime = this.formatDayBookingTime(resource?.DayBooking?.CheckOutTime);
            const syntheticCandidates = bookingTimes.filter(
                (bookingTime) => bookingTime.From === '00:00' && bookingTime.To === checkoutTime
            );
            const visibleBookingTimes = bookingTimes.filter(
                (bookingTime) => bookingTime.From !== '00:00' || bookingTime.To !== checkoutTime
            );

            if (!syntheticCandidates.length || !visibleBookingTimes.length) {
                return bookingTimes;
            }

            const syntheticBookingTime = this.buildSyntheticFollowingDayCheckoutBookingTime(
                visibleBookingTimes,
                resource
            );
            if (!syntheticBookingTime) {
                return bookingTimes;
            }

            return bookingTimes.filter(
                (bookingTime) =>
                    bookingTime.From !== syntheticBookingTime.From ||
                    bookingTime.To !== syntheticBookingTime.To ||
                    !this.hasSameWeekdays(bookingTime.Weekdays || [], syntheticBookingTime.Weekdays)
            );
        },
        buildSerializedBookingTimes(resource: BookingResource): BookingResourceBookingTime[] {
            const bookingTimes = this.normalizeBookingTimes(resource.BookingTimes, resource) || [];
            const syntheticBookingTime = this.buildSyntheticFollowingDayCheckoutBookingTime(bookingTimes, resource);

            if (!syntheticBookingTime) {
                return bookingTimes;
            }

            return [...bookingTimes, syntheticBookingTime];
        },
        getDayBookingBaseDurationInMinutes(resource: BookingResource): number {
            // One day-based booking duration follows the configured check-in to checkout span.
            const checkIn = resource.DayBooking?.CheckInTime || { Hour: 0, Minute: 0 };
            const checkOut = resource.DayBooking?.CheckOutTime || { Hour: 0, Minute: 0 };
            const checkInMinutes = (Number(checkIn.Hour) || 0) * 60 + (Number(checkIn.Minute) || 0);
            const checkOutMinutes = (Number(checkOut.Hour) || 0) * 60 + (Number(checkOut.Minute) || 0);
            const checkoutOffset =
                resource.DayBooking?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay ? 1440 : 0;
            const baseDuration = checkoutOffset + checkOutMinutes - checkInMinutes;

            return baseDuration > 0 ? baseDuration : 1440;
        },
        convertDayBookingDaysToMinutes(dayCount: number, resource: BookingResource): number {
            if (dayCount <= 0) {
                return 0;
            }

            const normalizedDayCount = Math.max(1, Math.round(dayCount));
            return this.getDayBookingBaseDurationInMinutes(resource) + (normalizedDayCount - 1) * 1440;
        },
        normalizeDayBookingDurationInMinutes(value: number, resource: BookingResource): number {
            const normalizedValue = Number(value) || 0;

            if (normalizedValue <= 0) {
                return 0;
            }

            const baseDuration = this.getDayBookingBaseDurationInMinutes(resource);
            const dayCount = Math.max(1, Math.round(1 + (normalizedValue - baseDuration) / 1440));
            return this.convertDayBookingDaysToMinutes(dayCount, resource);
        },
        normalizeBookingDurationRange(resource: BookingResource): BookingResource {
            const normalizedResource = cloneBookingResource(resource);

            if (normalizedResource.BookingGranularity === BookingGranularityType.BookingGranularityDay) {
                // Final payload guard for loaded or duplicated resources that still carry hour-based day limits.
                normalizedResource.MinBookingDuration = this.normalizeDayBookingDurationInMinutes(
                    normalizedResource.MinBookingDuration,
                    normalizedResource
                );
                normalizedResource.MaxBookingDuration = this.normalizeDayBookingDurationInMinutes(
                    normalizedResource.MaxBookingDuration,
                    normalizedResource
                );
            }

            const minBookingDuration = Number(normalizedResource.MinBookingDuration) || 0;
            const maxBookingDuration = Number(normalizedResource.MaxBookingDuration) || 0;

            if (maxBookingDuration > 0 && maxBookingDuration < minBookingDuration) {
                // Guard the payload so mixed duration units cannot create an invalid min/max range.
                normalizedResource.MaxBookingDuration = minBookingDuration;
            }

            return normalizedResource;
        },
        serializeResource(): BookingResource {
            const payload = this.normalizeBookingDurationRange({
                ...this.resource,
                Currency: this.currency,
            });

            payload.NotifyEmails = (payload.NotifyEmails || []).filter((entry) => !!entry);
            payload.PriceInCentsPerHour = Math.round(Number(payload.PriceInCentsPerHour) || 0);
            payload.BookingTimes = this.buildSerializedBookingTimes(payload);

            return payload;
        },
        handleImageSelected(file: File | null) {
            this.selectedImageFile = file;
        },
        async handleSubmit() {
            this.saving = true;
            try {
                await this.persistResource();
            } finally {
                this.saving = false;
            }
        },
        async handleCalendarSyncRequest(calendarId: string) {
            if (!calendarId || !this.isEditMode) {
                return;
            }

            this.saving = true;
            try {
                const resourceId = await this.persistResource();
                if (!resourceId) {
                    throw new Error('Resource id is required to start calendar synchronization');
                }

                await this.startCalendarSync(resourceId, calendarId);
            } finally {
                this.saving = false;
            }
        },
        handleCancel() {
            this.$router.push('/admin/booking/resource');
        },
        async uploadImage() {
            try {
                const data = await createImageUploadFormData(this.selectedImageFile as File);
                const response = await axios({
                    method: 'POST',
                    url: '/upload/image/booking-resources',
                    data,
                    withCredentials: true,
                });

                if (!response?.data?.url) {
                    EventBus.$emit('ERROR', {
                        Message: this.$t('messages.uploadImageFailed'),
                    });
                    throw new Error('Upload did not return an image url');
                }

                this.resource = cloneBookingResource({
                    ...this.resource,
                    ImageURL: response.data.url,
                });
                this.selectedImageFile = null;
            } catch (error) {
                const errorMsg = {
                    Message: this.$t('messages.imageUploadError'),
                    Details: error,
                };

                if (
                    error?.response &&
                    error.response.data &&
                    error.response.data.message === 'Request Entity Too Large'
                ) {
                    errorMsg.Message = this.$t('messages.imageUploadSizeError');
                }

                EventBus.$emit('ERROR', errorMsg);
                throw error;
            }
        },
        async persistResource(): Promise<string | null> {
            if (this.selectedImageFile) {
                await this.uploadImage();
            }

            if (this.isEditMode) {
                return this.updateResource();
            }

            return this.createResource();
        },
        async createResource(): Promise<string> {
            try {
                const response = await axios({
                    method: 'POST',
                    url: '/admin/booking/resource',
                    data: JSON.stringify(this.serializeResource()),
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                EventBus.$emit('INFO', {
                    Message: this.$t('messages.resourceCreated'),
                    Details: '',
                });

                this.$router.push(`/admin/booking/resource/edit/${response.data.ID}`);
                return response.data.ID;
            } catch (error) {
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.resourceCreateFailed'),
                    Details: error,
                });
                throw error;
            }
        },
        async updateResource(): Promise<string> {
            const resourceId = (this.resource.ID || this.$route.params.id) as string;
            try {
                await axios({
                    method: 'PUT',
                    url: '/admin/booking/resource',
                    data: JSON.stringify(this.serializeResource()),
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                EventBus.$emit('INFO', {
                    Message: this.$t('messages.changessaved'),
                    Details: '',
                });

                if (this.isEditMode) {
                    await this.getResource();
                }

                return resourceId;
            } catch (error) {
                EventBus.$emit('ERROR', {
                    Message: this.$t('errors.failedToSaveChanges'),
                    Details: error,
                });
                throw error;
            }
        },
        async startCalendarSync(resourceId: string, calendarId: string) {
            try {
                await axios({
                    method: 'POST',
                    url: `/admin/booking/sync/${resourceId}`,
                    data: {
                        calendar_id: calendarId,
                    },
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                await this.getResource();

                EventBus.$emit('INFO', {
                    Message: this.$t('messages.resourceSyncStarted'),
                    Details: '',
                });
            } catch (error) {
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.calendarSyncStartFailed'),
                    Details: error,
                });
                throw error;
            }
        },
        async listLocations() {
            try {
                const locations = await this.$store.dispatch('listLocations');
                this.locations = locations;
            } catch (error) {
                console.error(error);
            }
        },
        async getTaxRates() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/admin/tax-rate/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response?.data) {
                    this.taxRates = response.data;
                }
            } catch (error) {
                console.log(error);
            }
        },
        async listResources() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/admin/booking/resource/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                // Keep an empty first-use response from leaving stale linked-resource options behind.
                const resources = Array.isArray(response?.data?.Resources) ? response.data.Resources : [];
                this.resources = resources.map((resource) => this.normalizeResource(resource));
            } catch (error) {
                console.log(error);
            }
        },
        async getCalendarTokens() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/admin/oauth2/google/calendar/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                this.calendarTokens = response?.data?.Tokens || [];
                await this.prefetchCalendars();
            } catch (error) {
                console.log(error);
            }
        },
        async prefetchCalendars() {
            if (!this.calendarTokens.length) {
                this.calendarOptions = [];
                return;
            }

            // Prefetch calendars for each token so the route wrapper can stay thin
            // and the form can switch accounts without additional parent events.
            const calendarResponses = await settlePromises<
                AxiosResponse<{ Calendars?: BookingResourceExternalCalendar[] }>
            >(
                this.calendarTokens.map((token) =>
                    axios({
                        method: 'GET',
                        url: `/admin/booking/resource/external-calendars/${token.ID}`,
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    })
                )
            );

            this.calendarOptions = calendarResponses.reduce((result, response, index) => {
                // Keep calendars from successful token requests even when another token fails.
                if (response.status !== 'fulfilled' || !Array.isArray(response.value?.data?.Calendars)) {
                    return result;
                }

                const tokenId = this.calendarTokens[index]?.ID;
                const calendars = response.value.data.Calendars.map((calendar) => ({
                    ...calendar,
                    TokenID: calendar.TokenID || calendar.tokenID || tokenId,
                    tokenID: calendar.tokenID || calendar.TokenID || tokenId,
                }));

                return result.concat(calendars);
            }, [] as BookingResourceExternalCalendar[]);
        },
        async fetchPlans() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/admin/plan/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                this.plans = response?.data?.Plans || [];
            } catch (error) {
                console.log(error);
            }
        },
        async getUpgrades() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/admin/extra/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                this.availableUpgrades = response?.data?.Extras || [];
            } catch (error) {
                console.log(error);
            }
        },
        async listLocks() {
            try {
                const response = await axios.get('/admin/space/access/lock/list');
                this.availableLocks = response?.data || [];
            } catch (error) {
                console.log(error);
            }
        },
        async getResource() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: `/admin/booking/resource/${this.$route.params.id}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response?.data) {
                    this.resource = this.normalizeResource(response.data);
                    this.resourceLoaded = true;
                }
            } catch (error) {
                console.log(error);
            }
        },
    },
};
